import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { chunkedUpload, shouldUseChunkedUpload } from '../lib/chunkedUpload';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Upload, Trash2, Mic, Square, Play, Pause,
  ExternalLink, Image, FileText, Star, TrendingUp, Download, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';

const STATUSES = ["Draft", "In Progress", "Review", "Completed", "Archived"];
const CATEGORIES = ["Value Prop", "Price Comparison", "Process Oriented", "Testimonials", "UGC/Lifestyle", "Product Based"];
const POST_TYPES = ["AJ Talking Head", "Reel", "Static", "Carousel"];

const statusColors = {
  Draft: 'bg-zinc-100 text-zinc-600',
  'In Progress': 'bg-blue-50 text-blue-700',
  Review: 'bg-amber-50 text-amber-700',
  Completed: 'bg-emerald-50 text-emerald-700',
  Archived: 'bg-zinc-100 text-zinc-400',
};

export default function AdsCreativeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creative, setCreative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [newRef, setNewRef] = useState('');
  const [uploadingVisual, setUploadingVisual] = useState(false);
  const [uploadingFinal, setUploadingFinal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recording, setRecording] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const visualInputRef = useRef(null);
  const finalInputRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const loadCreative = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/ads/creatives/${id}`);
      setCreative(res.data);
      setForm({
        title: res.data.title || '',
        overall_concept: res.data.overall_concept || '',
        category: res.data.category || 'Value Prop',
        post_type: res.data.post_type || 'Static',
        status: res.data.status || 'Draft',
        ad_copy: res.data.ad_copy || '',
        design_brief: res.data.design_brief || '',
        references: res.data.references || [],
        score: res.data.score || null,
        roas: res.data.roas || null,
        learnings: res.data.learnings || '',
      });
    } catch {
      toast.error('Failed to load creative');
      navigate('/ads');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadCreative(); }, [loadCreative]);

  // Auto-save with debounce
  const triggerAutoSave = useCallback((updatedForm) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await api.patch(`/ads/creatives/${id}`, updatedForm);
      } catch { /* silent */ }
    }, 1500);
  }, [id]);

  const updateField = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    triggerAutoSave(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      await api.patch(`/ads/creatives/${id}`, form);
      toast.success('Saved');
      loadCreative();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // References
  const addReference = () => {
    if (!newRef.trim()) return;
    const refs = [...(form.references || []), newRef.trim()];
    updateField('references', refs);
    setNewRef('');
  };
  const removeReference = (idx) => {
    const refs = form.references.filter((_, i) => i !== idx);
    updateField('references', refs);
  };

  // Visual upload
  // Visual upload — chunked for large files
  const handleVisualUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingVisual(true);
      setUploadProgress(0);
      if (shouldUseChunkedUpload(file)) {
        await chunkedUpload(file, 'creative_visual', id, setUploadProgress);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/ads/creatives/${id}/visuals`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Visual uploaded');
      loadCreative();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingVisual(false);
      setUploadProgress(0);
      if (visualInputRef.current) visualInputRef.current.value = '';
    }
  };

  const handleDeleteVisual = async (visualId) => {
    try {
      await api.delete(`/ads/creatives/${id}/visuals/${visualId}`);
      toast.success('Deleted');
      loadCreative();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // Final asset upload — chunked for large files
  const handleFinalUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingFinal(true);
      setUploadProgress(0);
      if (shouldUseChunkedUpload(file)) {
        await chunkedUpload(file, 'creative_final', id, setUploadProgress);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/ads/creatives/${id}/final-asset`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Final asset uploaded — status set to Completed');
      loadCreative();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingFinal(false);
      setUploadProgress(0);
      if (finalInputRef.current) finalInputRef.current.value = '';
    }
  };

  // Voice note recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], 'voice_note.webm', { type: 'audio/webm' });
        try {
          setUploadingVoice(true);
          const fd = new FormData();
          fd.append('file', file);
          await api.post(`/ads/creatives/${id}/voice-note`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          toast.success('Voice note saved');
          loadCreative();
        } catch {
          toast.error('Failed to save voice note');
        } finally {
          setUploadingVoice(false);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  // Link preview helper
  const getLinkDomain = (url) => {
    try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
  };

  const handleDownloadFinal = async () => {
    try {
      const response = await api.get(`/ads/creatives/${id}/final-asset/download`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = creative?.final_asset?.original_name || 'final_asset';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-zinc-400">Loading...</div>
      </div>
    );
  }

  if (!creative) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="creative-detail-page">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ads')} data-testid="back-to-ads">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <Input
            value={form.title}
            onChange={e => updateField('title', e.target.value)}
            className="text-xl font-semibold border-none shadow-none px-0 h-auto bg-transparent text-zinc-900 focus-visible:ring-0"
            data-testid="creative-detail-title"
          />
        </div>
        <Badge className={cn("shrink-0", statusColors[form.status])}>{form.status}</Badge>
        <Button onClick={handleSave} disabled={saving} className="bg-zinc-900 text-white hover:bg-zinc-800" data-testid="save-creative-btn">
          <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Meta bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-zinc-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-zinc-500">Status</Label>
          <Select value={form.status} onValueChange={v => updateField('status', v)}>
            <SelectTrigger className="h-8 w-32 text-xs bg-white border-zinc-200 text-zinc-900" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white text-zinc-900">
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-zinc-500">Category</Label>
          <Select value={form.category} onValueChange={v => updateField('category', v)}>
            <SelectTrigger className="h-8 w-40 text-xs bg-white border-zinc-200 text-zinc-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white text-zinc-900">
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-zinc-500">Post Type</Label>
          <Select value={form.post_type} onValueChange={v => updateField('post_type', v)}>
            <SelectTrigger className="h-8 w-36 text-xs bg-white border-zinc-200 text-zinc-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white text-zinc-900">
              {POST_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Label className="text-xs text-zinc-500">Score</Label>
          <Input
            type="number" min={1} max={10}
            value={form.score || ''}
            onChange={e => updateField('score', e.target.value ? parseInt(e.target.value) : null)}
            className="h-8 w-16 text-xs text-center bg-white border-zinc-200 text-zinc-900"
            placeholder="1-10"
            data-testid="score-input"
          />
          <Label className="text-xs text-zinc-500">ROAS</Label>
          <Input
            type="number" step="0.1" min={0}
            value={form.roas || ''}
            onChange={e => updateField('roas', e.target.value ? parseFloat(e.target.value) : null)}
            className="h-8 w-20 text-xs text-center bg-white border-zinc-200 text-zinc-900"
            placeholder="0.0x"
            data-testid="roas-input"
          />
        </div>
      </div>

      {/* Overall Concept */}
      <Section title="Overall Concept" icon={<FileText size={16} />}>
        <Textarea
          value={form.overall_concept}
          onChange={e => updateField('overall_concept', e.target.value)}
          rows={3}
          placeholder="Describe the creative concept..."
          className="bg-white border-zinc-200 text-zinc-900 text-sm"
          data-testid="concept-textarea"
        />
      </Section>

      {/* Ad Copy */}
      <Section title="Ad Copy" icon={<FileText size={16} />}>
        <Textarea
          value={form.ad_copy}
          onChange={e => updateField('ad_copy', e.target.value)}
          rows={4}
          placeholder="Write the ad copy text..."
          className="bg-white border-zinc-200 text-zinc-900 text-sm"
          data-testid="adcopy-textarea"
        />
      </Section>

      {/* Design Brief + Voice Note */}
      <Section title="Design Brief" icon={<FileText size={16} />}>
        <Textarea
          value={form.design_brief}
          onChange={e => updateField('design_brief', e.target.value)}
          rows={4}
          placeholder="Describe the design direction, layout, colors..."
          className="bg-white border-zinc-200 text-zinc-900 text-sm"
          data-testid="designbrief-textarea"
        />
        <div className="mt-3 flex items-center gap-3">
          {recording ? (
            <Button variant="outline" size="sm" onClick={stopRecording} className="text-red-600 border-red-200 bg-red-50" data-testid="stop-recording-btn">
              <Square size={14} className="mr-1.5 fill-red-600" /> Stop Recording
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={startRecording} disabled={uploadingVoice} className="text-zinc-700 border-zinc-200" data-testid="start-recording-btn">
              <Mic size={14} className="mr-1.5" /> {uploadingVoice ? 'Saving...' : 'Record Voice Note'}
            </Button>
          )}
          {creative.voice_note?.download_url && (
            <>
              <audio controls className="h-8" src={creative.voice_note.download_url} data-testid="voice-note-player" />
              <a
                href={creative.voice_note.download_url}
                download={creative.voice_note.original_name || 'voice_note.webm'}
                className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:text-zinc-900 border border-zinc-200 rounded px-2 py-1"
                data-testid="download-voice-note-btn"
                title="Download voice note"
              >
                <Download size={12} /> Download
              </a>
            </>
          )}
        </div>
      </Section>

      {/* References */}
      <Section title="References" icon={<ExternalLink size={16} />}>
        <div className="flex gap-2">
          <Input
            value={newRef}
            onChange={e => setNewRef(e.target.value)}
            placeholder="Paste a Meta Ads Library or Instagram link..."
            className="bg-white border-zinc-200 text-zinc-900 text-sm flex-1"
            data-testid="reference-input"
            onKeyDown={e => e.key === 'Enter' && addReference()}
          />
          <Button variant="outline" onClick={addReference} className="text-zinc-700 border-zinc-200" data-testid="add-reference-btn">Add</Button>
        </div>
        {form.references?.length > 0 && (
          <div className="mt-3 space-y-2">
            {form.references.map((ref, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
                <ExternalLink size={14} className="text-zinc-400 shrink-0" />
                <a
                  href={ref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline truncate flex-1"
                >
                  {getLinkDomain(ref)} &mdash; {ref.length > 60 ? ref.slice(0, 60) + '...' : ref}
                </a>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-400 hover:text-red-600" onClick={() => removeReference(idx)}>
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Visuals Upload */}
      <Section title="Visuals" icon={<Image size={16} />}>
        <input type="file" ref={visualInputRef} className="hidden" accept="image/*,video/*" onChange={handleVisualUpload} />
        <Button
          variant="outline"
          onClick={() => visualInputRef.current?.click()}
          disabled={uploadingVisual}
          className="text-zinc-700 border-zinc-200"
          data-testid="upload-visual-btn"
        >
          <Upload size={14} className="mr-1.5" /> {uploadingVisual ? (uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Uploading...') : 'Upload Visual'}
        </Button>
        {creative.visuals?.length > 0 && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {creative.visuals.map(v => (
              <div key={v.id} className="relative group rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50">
                {v.content_type?.startsWith('video') ? (
                  <video src={v.download_url} className="w-full h-32 object-cover" />
                ) : (
                  <img src={v.download_url} alt={v.original_name} className="w-full h-32 object-cover" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a
                    href={v.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-white/90 hover:bg-white text-zinc-800"
                    title="View in new tab"
                    data-testid={`view-visual-${v.id}`}
                  >
                    <Eye size={14} />
                  </a>
                  <a
                    href={v.download_url}
                    download={v.original_name}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-white/90 hover:bg-white text-zinc-800"
                    title="Download"
                    data-testid={`download-visual-${v.id}`}
                  >
                    <Download size={14} />
                  </a>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 bg-white/90 hover:bg-white text-red-600"
                    onClick={() => handleDeleteVisual(v.id)}
                    title="Delete"
                    data-testid={`delete-visual-${v.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 p-1.5 truncate">{v.original_name}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Final Asset */}
      <Section title="Final Asset" icon={<Star size={16} />}>
        <input type="file" ref={finalInputRef} className="hidden" accept="image/*,video/*,.mp4,.mov,.avi,.mkv" onChange={handleFinalUpload} />
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => finalInputRef.current?.click()}
            disabled={uploadingFinal}
            className="text-zinc-700 border-zinc-200"
            data-testid="upload-final-btn"
          >
            <Upload size={14} className="mr-1.5" /> {uploadingFinal ? (uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Uploading...') : 'Upload Final Asset'}
          </Button>
          {creative.final_asset?.download_url && (
            <>
              <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 bg-emerald-50" onClick={() => window.open(creative.final_asset.download_url, '_blank')} data-testid="view-final-asset-btn">
                <Eye size={14} className="mr-1" /> View
              </Button>
              <Button variant="outline" size="sm" className="text-zinc-700 border-zinc-200" onClick={handleDownloadFinal} data-testid="download-final-asset-btn">
                <Download size={14} className="mr-1" /> Download
              </Button>
            </>
          )}
        </div>
        {creative.final_asset?.download_url && creative.final_asset?.content_type?.startsWith('image') && (
          <div className="mt-3 border border-zinc-200 rounded-lg overflow-hidden inline-block">
            <img src={creative.final_asset.download_url} alt="Final" className="max-h-64 object-contain" />
          </div>
        )}
      </Section>

      {/* Learnings */}
      <Section title="Learnings / Notes" icon={<TrendingUp size={16} />}>
        <Textarea
          value={form.learnings}
          onChange={e => updateField('learnings', e.target.value)}
          rows={3}
          placeholder="What worked? What didn't? Key takeaways..."
          className="bg-white border-zinc-200 text-zinc-900 text-sm"
          data-testid="learnings-textarea"
        />
      </Section>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`section-${title.toLowerCase().replace(/[\s/]/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-zinc-500">{icon}</span>
        <h3 className="text-sm font-semibold text-zinc-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}
